import React from 'react';

import {shouldDisableReviewLockingActions} from 'notebook/reviews/disableAuthorActions';
import postReviewActionServerUpdate from 'notebook/reviews/postReviewActionServerUpdate';
import ReviewBadge from 'notebook/reviews/ReviewBadge';
import {ReviewAction} from 'notebook/reviews/ReviewConstants';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';

interface EntryNoteTitleBadgeProps {
  enableReviewLifecycle: boolean;
  entryId: string;
  hasEnterprisePlan: boolean;
}

export function getReviewError(reviewableId: string, action?: ReviewAction): string | null {
  // Check if entry exists in store
  if (!EntryStore.inStore(reviewableId)) {
    return 'Entry could not be found';
  }

  const entryHasUnsavedChanges = EntryStore.hasUnsavedChanges(reviewableId) ?? false;
  // It is possible for entryStore.inStore(reviewableId) to pass before the entry itself is fully
  // loaded because the VersionedItemSubstore registers the reviewableId before the entry itself (see
  // EntryActions.open). We therefore need to check if the entry is loaded before accessing the note;
  // if not, we should disable sending for review.
  const note = EntryStore.getEntry(reviewableId)?.note;

  // Errors that should block ALL actions:
  // - Unsaved changes must be saved before any review action
  // - Document must be loaded before any review action
  if (entryHasUnsavedChanges) {
    return 'Entry has unsaved changes';
  }

  if (note === undefined) {
    return 'Document not yet loaded';
  }

  // RETRACT actions (reopen, cancel) are allowed even with unsubmitted tables.
  // Only check table validation for other actions (SEND_FOR_REVIEW, ACCEPT, etc).
  if (action === ReviewAction.RETRACT) {
    return null;
  }

  // For non-RETRACT actions, check table validation (unsubmitted tables, loading cells, etc)
  const {disableAuthorActionsTooltip} = shouldDisableReviewLockingActions(note);
  return disableAuthorActionsTooltip;
}

function EntryNoteTitleBadge({
  enableReviewLifecycle,
  entryId,
  hasEnterprisePlan,
}: EntryNoteTitleBadgeProps): JSX.Element | null {
  if (!hasEnterprisePlan) {
    return null;
  }
  return (
    <ReviewBadge
      className="noteEditor-reviewBadge"
      reviewableId={entryId}
      enableReviewLifecycle={enableReviewLifecycle}
      onReviewChange={postReviewActionServerUpdate}
      getSendForReviewValidationError={getReviewError}
    />
  );
}

export default React.memo(EntryNoteTitleBadge);
